import { sum } from 'lodash';
import { dangerouslySetTableElementWidthPx } from '../../domUtils/hotTableDomMutationUtils';
import { getHotTableColumnWidthsPx } from './hotTableManualColumnResizeUtils';
import { MNHotInstance } from '../../../tableHotInstanceTypes';

type ColumnWidthChanges = { column: number; widthPx: number }[];
type ApplyManualColumnWidthsOptions = { shouldSetTableElementDOMWidth?: boolean };

/**
 * Applies manual widths through Handsontable's ManualColumnResize plugin, syncs the wrapper width,
 * and re-renders the table on the next animation frame.
 */
export const applyManualColumnWidths = (
    hotInstance: MNHotInstance,
    columnWidthChanges: ColumnWidthChanges,
    options?: ApplyManualColumnWidthsOptions,
): boolean => {
    if (!columnWidthChanges.length) return false;

    const columnWidthsPx = getHotTableColumnWidthsPx(hotInstance);
    if (columnWidthChanges.every(({ column, widthPx }) => columnWidthsPx[column] === widthPx)) return false;

    const manualColumnResizePlugin = hotInstance.getPlugin<'manualColumnResize'>('manualColumnResize');

    if (!manualColumnResizePlugin) return false;

    const newColumnWidthsPx = [...columnWidthsPx];

    columnWidthChanges.forEach(({ column, widthPx }) => {
        manualColumnResizePlugin.setManualSize(column, widthPx);
        newColumnWidthsPx[column] = widthPx;
    });

    const { shouldSetTableElementDOMWidth = false } = options || {};
    if (shouldSetTableElementDOMWidth) {
        dangerouslySetTableElementWidthPx(hotInstance, sum(newColumnWidthsPx));
    }

    requestAnimationFrame(() => {
        hotInstance.render();
    });

    return true;
};

/**
 * Run applyManualColumnWidths, and call Handsontable's resize lifecycle hooks
 * - beforeColumnResize
 * - afterColumnResize
 */
export const applyManualColumnWidthsWithHotHooks = (
    hotInstance: MNHotInstance,
    columnWidthChanges: ColumnWidthChanges,
    options?: ApplyManualColumnWidthsOptions,
): boolean => {
    if (!columnWidthChanges.length) return false;

    const nextColWidthsChanges = columnWidthChanges.map(({ column, widthPx }) => {
        const hookWidthPx = hotInstance.runHooks('beforeColumnResize', widthPx, column, false);

        return { column, widthPx: hookWidthPx ?? widthPx };
    });

    const didApplyWidths = applyManualColumnWidths(hotInstance, nextColWidthsChanges, options);
    if (!didApplyWidths) return false;

    nextColWidthsChanges.forEach(({ column, widthPx }) => {
        hotInstance.runHooks('afterColumnResize', widthPx, column, false);
    });

    return true;
};
